import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCalendar } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Option, Question } from '../../types';
import './FormInput.css';
import {
  DATE_FIELD_TYPE,
  DATETIME_LOCAL_FIELD_TYPE,
  INPUT_TEXT_SUSPICIOUS_PATTERN,
  NUMBER_FIELD_TYPE,
} from '../../consts/consts';
import { useLanguage } from '../../LanguageContext';
import { getValidationMessage } from '../../utils/validationMessages';
import {
  getVolunteerNameMismatchError,
  isVolunteerNameQuestionKey,
} from '../../utils/volunteerNameValidation';
import validator from 'validator';
import {
  DATE_FORMAT_ISRAEL,
  DATE_PLACEHOLDER_ISRAEL,
  formatDateToIsraelDisplay,
  isDateFieldType,
  normalizeToYYYYMMDD,
  YYYY_MM_DD_REGEX,
} from '../../utils/dateNormalization';

interface FormInputProps {
  question: Question;
  errors: Record<string, string>;
  setErrors: (
    value: ((prevState: Record<string, string>) => Record<string, string>) | Record<string, string>,
  ) => void;
  handleChange: (questionKey: string, answer: Option[]) => void;
  calculatedValue: number | null;
  disabled: boolean;
  allQuestions?: Question[];
}

/** Stable custom input for date picker (must be outside FormInput so it is not recreated each render, which would break typing). */
const DateInputWithButton = React.forwardRef<
  HTMLInputElement,
  {
    displayValue: string;
    onDisplayChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    hasError: boolean;
    errorId: string | undefined;
    disabled: boolean;
    onOpenPicker: () => void;
    inputProps: Record<string, unknown>;
  } & React.InputHTMLAttributes<HTMLInputElement>
>(
  (
    props: {
      displayValue: string;
      onDisplayChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      onInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
      hasError: boolean;
      errorId: string | undefined;
      disabled: boolean;
      onOpenPicker: () => void;
      inputProps: Record<string, unknown>;
    } & React.InputHTMLAttributes<HTMLInputElement>,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const {
      displayValue,
      onDisplayChange,
      onInputBlur,
      hasError,
      errorId,
      disabled,
      onOpenPicker,
      inputProps,
      ...rest
    } = props;
    return (
      <div className='question-input-date-wrapper'>
        <input
          ref={ref}
          {...rest}
          {...inputProps}
          value={displayValue}
          onChange={onDisplayChange}
          onBlur={onInputBlur}
          type='text'
          aria-invalid={hasError}
          aria-describedby={errorId}
        />
        <button
          type='button'
          className='question-input-date-picker-button'
          onClick={onOpenPicker}
          aria-label='Open calendar'
          tabIndex={-1}
          disabled={disabled}
        >
          <FaCalendar />
        </button>
      </div>
    );
  },
);
DateInputWithButton.displayName = 'DateInputWithButton';

const FormInput: React.FC<FormInputProps> = ({
  question,
  errors,
  setErrors,
  handleChange,
  calculatedValue,
  disabled,
  allQuestions = [],
}) => {
  const { language } = useLanguage();
  const [inputValue, setInputValue] = useState<string | number>('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const datePickerWrapperRef = useRef<HTMLDivElement>(null);

  // Open question with expected value = date → render type="date" and validate date-only
  const isDateField = isDateFieldType(question.field_type);
  const isFieldDisabled = disabled || calculatedValue !== null || !isDependencyAnswered();

  useEffect(() => {
    const initialText = calculatedValue ?? question.answer?.[0]?.text ?? '';
    // Strip time part for datetime-local type
    const raw =
      question.field_type === DATETIME_LOCAL_FIELD_TYPE
        ? String(initialText).split('T')[0]
        : initialText;
    // Normalize date fields to YYYY-MM-DD so the picker and validation always see a canonical value
    const normalized = isDateField
      ? (normalizeToYYYYMMDD(String(raw), { preferDayFirst: true }) ?? raw)
      : raw;
    setInputValue(normalized);
  }, [calculatedValue, question.answer, question.field_type]); // eslint-disable-line react-hooks/exhaustive-deps

  function isDependencyAnswered(): boolean {
    if (!question.answer_depends_on_question || !question.dependency_question_guid) {
      return true;
    }
    const dep = allQuestions.find((q) => q.guid === question.dependency_question_guid);
    return !!dep?.answer?.[0]?.text;
  }

  function getPlainText(htmlText: string): string {
    const div = document.createElement('div');
    div.innerHTML = htmlText;
    return div.textContent || div.innerText || '';
  }

  function clearError(): void {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[question.question_key];
      return next;
    });
  }

  function setError(message: string): void {
    setErrors((prev) => ({ ...prev, [question.question_key]: message }));
  }

  // ── Dependency / date-comparison validators ──────────────────────────────

  function validateCheckboxDateDependency(value: string | number): string {
    if (!isDateField || !question.date_comparison_enabled || !question.date_comparison_operator) {
      return '';
    }
    const inputDate = new Date(String(value));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);
    if (isNaN(inputDate.getTime())) return '';
    if (question.date_comparison_operator === 'greater_than' && inputDate < today)
      return getValidationMessage(language, 'date_from_today');
    if (question.date_comparison_operator === 'less_than' && inputDate > today)
      return getValidationMessage(language, 'date_no_future');
    return '';
  }

  function validateDependency(value: string | number): string {
    if (
      !question.answer_depends_on_question ||
      !question.dependency_question_guid ||
      !question.dependency_operator
    ) {
      return '';
    }
    const dep = allQuestions.find((q) => q.guid === question.dependency_question_guid);
    if (!dep?.answer?.[0]?.text) return '';
    const depText = String(dep.answer[0].text);
    const curText = String(value);
    const depLabel = getPlainText(dep.text);
    const depParams = { depLabel, depValue: depText };
    if (isDateField) {
      const depDate = new Date(depText);
      const curDate = new Date(curText);
      if (isNaN(depDate.getTime()) || isNaN(curDate.getTime())) return '';
      if (
        (question.dependency_operator === 'greater_than' ||
          question.dependency_operator === 'greater_equal') &&
        curDate < depDate
      )
        return getValidationMessage(language, 'date_before_dep', depParams);
      if (question.dependency_operator === 'less_than' && curDate >= depDate)
        return getValidationMessage(language, 'date_after_or_equal_dep', depParams);
      if (question.dependency_operator === 'less_equal' && curDate > depDate)
        return getValidationMessage(language, 'date_after_dep', depParams);
    } else {
      const depNum = parseFloat(depText);
      const curNum = parseFloat(curText);
      if (isNaN(depNum) || isNaN(curNum)) return '';
      const numDepParams = { depLabel, depValue: depNum };
      if (
        (question.dependency_operator === 'greater_than' ||
          question.dependency_operator === 'greater_equal') &&
        curNum < depNum
      )
        return getValidationMessage(language, 'number_before_dep', numDepParams);
      if (question.dependency_operator === 'less_than' && curNum >= depNum)
        return getValidationMessage(language, 'number_after_or_equal_dep', numDepParams);
      if (question.dependency_operator === 'less_equal' && curNum > depNum)
        return getValidationMessage(language, 'number_after_dep', numDepParams);
    }
    return '';
  }

  // ── Shared change handler (called after normalization) ───────────────────

  function commitValue(value: string | number): void {
    const isEmpty = value === '' || value === null || value === undefined;
    let errorMessage = '';

    if (question.field_type === NUMBER_FIELD_TYPE && !isEmpty) {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (!isNaN(num)) {
        if (question.min_value !== undefined && question.max_value !== undefined) {
          if (num < question.min_value || num > question.max_value)
            errorMessage = getValidationMessage(language, 'number_between', {
              min: question.min_value,
              max: question.max_value,
            });
        } else if (question.min_value !== undefined && num < question.min_value) {
          errorMessage = getValidationMessage(language, 'number_min', {
            min: question.min_value,
          });
        } else if (question.max_value !== undefined && num > question.max_value) {
          errorMessage = getValidationMessage(language, 'number_max', {
            max: question.max_value,
          });
        }
        if (!errorMessage) errorMessage = validateDependency(value);
        if (!errorMessage) errorMessage = validateCheckboxDateDependency(value);
      } else {
        errorMessage = getValidationMessage(language, 'invalid_number');
      }
    } else if (!isEmpty) {
      errorMessage = validateDependency(value);
      if (!errorMessage) errorMessage = validateCheckboxDateDependency(value);
    }

    const volunteerNameMismatch =
      !errorMessage && !isEmpty && isVolunteerNameQuestionKey(question.question_key)
        ? getVolunteerNameMismatchError(allQuestions, String(value), language)
        : '';

    if (errorMessage) {
      setError(errorMessage);
      return;
    }

    if (isEmpty) {
      clearError();
      handleChange(question.question_key, []);
      return;
    }

    handleChange(question.question_key, [{ text: String(value), guid: question.guid }]);
    if (volunteerNameMismatch) {
      setError(volunteerNameMismatch);
    } else {
      clearError();
    }
  }

  // ── Input handlers ───────────────────────────────────────────────────────

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    if (errors[question.question_key] && String(v).trim() !== '') {
      clearError();
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setPickerOpen(false);
    let value = e.target.value;

    if (isDateField && value.trim() !== '') {
      const normalized = normalizeToYYYYMMDD(value, { preferDayFirst: true });
      if (normalized !== null) {
        e.target.value = normalized;
        value = normalized;
      }
    }

    if (isDateField && value.trim() !== '') {
      const isValidFormat = validator.isDate(value, {
        format: 'YYYY-MM-DD',
        strictMode: true,
      });
      const parsed = new Date(value);
      const isRealisticDate =
        !isNaN(parsed.getTime()) && parsed.getFullYear() >= 1900 && parsed.getFullYear() <= 2100;
      if (!isValidFormat || !isRealisticDate) {
        setError(getValidationMessage(language, 'date_invalid'));
        return;
      }
    }

    if (INPUT_TEXT_SUSPICIOUS_PATTERN.test(value)) {
      setError(getValidationMessage(language, 'invalid_input'));
      return;
    }

    clearError();
    setInputValue(value);
    commitValue(value);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!isDateField) return;
    const pastedText = e.clipboardData.getData('text');
    const normalized = normalizeToYYYYMMDD(pastedText, {
      preferDayFirst: true,
    });
    if (normalized !== null) {
      e.preventDefault();
      setInputValue(normalized);
      e.currentTarget.value = normalized;
      commitValue(normalized);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
    e.preventDefault();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value !== String(inputValue)) e.target.value = String(inputValue);
  };

  // Close date picker when clicking outside (input/button or calendar dropdown)
  useEffect(() => {
    if (!pickerOpen || !isDateField) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (datePickerWrapperRef.current?.contains(target)) return;
      const popper = document.querySelector('.react-datepicker-popper');
      if (popper?.contains(target)) return;
      setPickerOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [pickerOpen, isDateField]);

  // ── Date picker: selected date so calendar opens on the written/picked date (support both YYYY-MM-DD and DD/MM/YYYY) ──

  const datePickerSelected = useMemo((): Date | null => {
    if (!isDateField || String(inputValue).trim() === '') return null;
    const str = String(inputValue).trim();
    if (YYYY_MM_DD_REGEX.test(str)) {
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d;
    }
    const parts = str
      .split(/[-/.]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length !== 3 || parts[2].length !== 4) return null;
    const normalized = normalizeToYYYYMMDD(str, { preferDayFirst: true });
    if (normalized === null) return null;
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }, [isDateField, inputValue]);

  // Show date always in Israel format (DD/MM/YYYY) when we have a valid date; show raw only while year is incomplete when typing DD/MM/YYYY
  const dateDisplayValue = useMemo((): string => {
    if (!isDateField) return String(inputValue);
    const str = String(inputValue).trim();
    if (YYYY_MM_DD_REGEX.test(str)) {
      return formatDateToIsraelDisplay(str);
    }
    const parts = str
      .split(/[-/.]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 3 && parts[2].length !== 4) {
      return String(inputValue);
    }
    const normalized = normalizeToYYYYMMDD(str, { preferDayFirst: true });
    return normalized ? formatDateToIsraelDisplay(normalized) : String(inputValue);
  }, [isDateField, inputValue]);

  const handleDatePickerChange = (date: Date | null) => {
    if (date === null) return;
    const yyyyMmDd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setInputValue(yyyyMmDd);
    setPickerOpen(false);
    clearError();
    commitValue(yyyyMmDd);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const showMeasurementUnit =
    question.field_type === NUMBER_FIELD_TYPE &&
    question.measurement_unit != null &&
    String(question.measurement_unit).trim() !== '';

  const inputLength = String(inputValue).length;
  const unitSpanId = `unit-${question.question_key}`;

  const inputProps = {
    id: question.question_key,
    key: question.question_key,
    className: showMeasurementUnit ? 'question-input question-input-inside-unit' : 'question-input',
    type: isDateField ? 'text' : question.field_type,
    onChange: onInputChange,
    onBlur,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onWheel: handleWheel,
    onPaste,
    min: question.min_value,
    max: question.max_value,
    value: inputValue,
    disabled: isFieldDisabled,
    autoComplete: 'off' as const,
    autoCorrect: 'off',
    spellCheck: false as const,
    ...(showMeasurementUnit && {
      style: { width: `${Math.max(6, inputLength + 1)}ch`, minWidth: '6ch' },
      'aria-describedby': unitSpanId,
    }),
  };

  // Date field: Israel format DD/MM/YYYY; calendar opens only via button; store/send YYYY-MM-DD
  const dateInputElement = isDateField ? (
    <div ref={datePickerWrapperRef}>
      <DatePicker
        selected={datePickerSelected}
        onChange={handleDatePickerChange}
        open={pickerOpen}
        onCalendarClose={() => setPickerOpen(false)}
        onChangeRaw={(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
          const target = event?.target as HTMLInputElement | undefined;
          if (target && typeof target.value === 'string') {
            setInputValue(target.value);
            if (errors[question.question_key] && target.value.trim() !== '') clearError();
          }
        }}
        onBlur={onBlur}
        dateFormat={DATE_FORMAT_ISRAEL}
        showMonthDropdown
        showYearDropdown
        dropdownMode='select'
        yearDropdownItemNumber={120}
        scrollableYearDropdown
        placeholderText={DATE_PLACEHOLDER_ISRAEL}
        customInput={
          <DateInputWithButton
            displayValue={dateDisplayValue}
            onDisplayChange={onInputChange}
            onInputBlur={onBlur}
            hasError={!!errors[question.question_key]}
            errorId={
              errors[question.question_key] ? `${question.question_key}-date-error` : undefined
            }
            disabled={isFieldDisabled}
            onOpenPicker={() => setPickerOpen((prev) => !prev)}
            inputProps={inputProps}
          />
        }
      />
    </div>
  ) : (
    <input
      {...inputProps}
      type={
        question.field_type === DATETIME_LOCAL_FIELD_TYPE ? DATE_FIELD_TYPE : question.field_type
      }
    />
  );

  return (
    <div>
      {showMeasurementUnit ? (
        <div className='question-input-with-unit'>
          {dateInputElement}
          <span id={unitSpanId} className='question-input-unit' aria-hidden='true'>
            {question.measurement_unit}
          </span>
        </div>
      ) : (
        dateInputElement
      )}
      {errors[question.question_key] && (
        <span id={`${question.question_key}-date-error`} style={{ color: 'red' }}>
          {errors[question.question_key]}
        </span>
      )}
    </div>
  );
};

export default FormInput;
